import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { css } from '@emotion/react';
import { toast } from 'react-toastify';
import Page from '../Page';
import Header from '../Header';
import { spacing, fontStyles, colors, buildPageSpacing } from '../../styles';
import * as storage from '../../storage';
import { Allocation } from '../../storage';
import { toTitleCase } from '../../utils/symbolUtils';

interface LocationState {
  collectionId: string;
  collectionName: string;
  strategy?: string;
  allocations: Allocation[];
}

// Define styles outside component to prevent recreation on every render
const pageStyles = css`
  padding-top: ${spacing.deci};
`;

const headingStyles = css`
  ${fontStyles.shark};
  margin-bottom: ${spacing.deci};
`;

const subtitleStyles = css`
  ${fontStyles.dog};
  color: ${colors.silver};
  margin-bottom: 0;
`;

const listStyles = css`
  ${fontStyles.cat};
  list-style: none;
  padding: 0;
  margin-bottom: 200px; /* Extra padding to prevent overlap with fixed buttons */
`;

const rowStyles = css`
  padding: ${spacing.deci} 0;
  border-bottom: 0.5px solid var(--border-color);

  &:last-of-type {
    border-bottom: none;
  }
`;

const symbolStyles = css`
  ${fontStyles.roboCat};
  margin: 0;
  color: var(--link-color);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const rationaleStyles = css`
  ${fontStyles.thinFlea};
  margin: ${spacing.centi} 0 0 0;
  color: var(--text-color);
`;

const buttonContainerStyles = css`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: ${spacing.regular};
  padding-bottom: ${buildPageSpacing(spacing.regular, 'BOTTOM')};
  background-color: var(--background-color);
  border-top: 0.5px solid var(--border-color);
  z-index: 10;

  html[data-theme='dark'] & {
    background-color: ${colors.liquorice};
  }

  @media (min-width: 1024px) {
    width: 1024px;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const buttonStyles = css`
  display: block;
  width: 100%;
  margin-bottom: ${spacing.deci};
  padding: ${spacing.deci};
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ConfirmCollectionCopy: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { collectionId, collectionName, strategy, allocations } = location.state || {};
  const [isCreating, setIsCreating] = React.useState(false);

  // Handle missing state by redirecting in useEffect
  React.useEffect(() => {
    if (!collectionId || !collectionName || !allocations) {
      toast.error('Missing collection data. Please try again.');
      history.replace(collectionId ? `/collection/${collectionId}` : '/portfolios');
    }
  }, [collectionId, collectionName, allocations, history]);

  // Show nothing while redirecting to avoid flash of content
  if (!collectionId || !collectionName || !allocations) {
    return null;
  }

  const handleSave = async () => {
    if (isCreating) return; // Prevent double-click

    setIsCreating(true);
    try {
      const portfolioId = await storage.copyCollectionToPortfolio(collectionName, strategy || '', allocations);
      toast.success(`Portfolio created from ${collectionName}`);
      history.push(`/portfolios/view/${portfolioId}`);
    } catch (error) {
      console.error('Error copying collection to portfolio:', error);
      toast.error('Failed to create portfolio. Please try again.');
      setIsCreating(false); // Re-enable on error
    }
  };

  const handleDiscard = () => {
    history.goBack();
  };

  return (
    <Page showNavigationBar={false}>
      <Header headingText="" onBack={handleDiscard} />
      <div css={pageStyles}>
        <h1 css={headingStyles}>Copy Portfolio</h1>
        <p css={subtitleStyles}>
          Create a new portfolio from <strong>{collectionName}</strong>
        </p>
        <ul css={listStyles}>
          {allocations.map((alloc) => (
            <li css={rowStyles} key={alloc.symbol}>
              <p css={symbolStyles}>
                <span>
                  {alloc.symbol}
                  {alloc.name?.trim() && alloc.name.trim() !== alloc.symbol && (
                    <span
                      css={css`
                        margin-left: ${spacing.centi};
                        ${fontStyles.cat};
                        color: var(--secondary-text);
                      `}
                    >
                      {toTitleCase(alloc.name)}
                    </span>
                  )}
                </span>
                {alloc.allocation_percentage !== undefined && <span>{Math.round(alloc.allocation_percentage)}%</span>}
              </p>
              {alloc.rationale && <p css={rationaleStyles}>{alloc.rationale}</p>}
            </li>
          ))}
        </ul>
      </div>
      <div css={buttonContainerStyles}>
        <button
          css={css`
            ${buttonStyles};
            background-color: ${colors.petrolBlue};
            color: ${colors.snow};
            opacity: ${isCreating ? 0.6 : 1};
            cursor: ${isCreating ? 'not-allowed' : 'pointer'};
          `}
          onClick={handleSave}
          disabled={isCreating}
          aria-label={isCreating ? 'Creating portfolio...' : 'Create portfolio from collection'}
          aria-busy={isCreating}
        >
          {isCreating ? 'Creating...' : 'Create Portfolio'}
        </button>
        <button
          css={css`
            ${buttonStyles};
            background-color: ${colors.fog};
            color: ${colors.liquorice};
          `}
          onClick={handleDiscard}
          aria-label="Cancel and return to collection page"
        >
          Cancel
        </button>
      </div>
    </Page>
  );
};

export default ConfirmCollectionCopy;
